// Offline diagnostics tooling (development only)
// Provides visibility into cache, queue, and storage state

import { db } from '../db/database.js';
import { OfflineConfig } from '../config/offline-config.js';
import { offlineStateService } from '../services/offline-state.service.js';
import { mutationQueue } from '../queue/mutation-queue.js';
import { imageCacheService } from '../services/image-cache.service.js';
import { recoveryManager } from './recovery.js';
import { invalidationManager } from '../cache/invalidation-rules.js';

class OfflineDiagnostics {
  constructor() {
    this.logs = [];
    this.maxLogs = OfflineConfig.diagnostics.maxLogEntries;
    this.enabled = OfflineConfig.diagnostics.enabled;
  }

  /**
   * Log diagnostic event
   */
  log(level, category, message, data = {}) {
    if (!this.enabled) return;

    const logEntry = {
      timestamp: Date.now(),
      level,
      category,
      message,
      data,
    };

    this.logs.push(logEntry);

    // Keep only recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Console output based on log level
    if (OfflineConfig.diagnostics.logLevel === 'debug' || 
        (OfflineConfig.diagnostics.logLevel === 'error' && level === 'error')) {
      console.log(`[Offline:${category}]`, message, data);
    }
  }

  /**
   * Get comprehensive diagnostics
   */
  async getDiagnostics() {
    if (!this.enabled) {
      return { enabled: false, message: 'Diagnostics disabled in production' };
    }

    try {
      const diagnostics = {
        timestamp: Date.now(),
        network: await this.getNetworkDiagnostics(),
        storage: await this.getStorageDiagnostics(),
        cache: await this.getCacheDiagnostics(),
        queue: await this.getQueueDiagnostics(),
        images: await this.getImageDiagnostics(),
        recovery: await this.getRecoveryDiagnostics(),
        config: this.getConfigDiagnostics(),
        logs: this.getRecentLogs(),
      };

      return diagnostics;
    } catch (error) {
      this.log('error', 'diagnostics', 'Failed to gather diagnostics', { error });
      return { error: error.message };
    }
  }

  /**
   * Get network diagnostics
   */
  async getNetworkDiagnostics() {
    const state = offlineStateService.getState();

    return {
      isOnline: state.isOnline,
      status: state.status,
      lastOnlineTime: state.lastOnlineTime,
      lastOfflineTime: state.lastOfflineTime,
      timeSinceLastOnline: state.timeSinceLastOnline,
      timeSinceLastOffline: state.timeSinceLastOffline,
      pendingOperations: state.pendingOperations,
      reconnectAttempts: state.reconnectAttempts,
      effectiveType: navigator.connection?.effectiveType || 'unknown',
      downlink: navigator.connection?.downlink || 'unknown',
      rtt: navigator.connection?.rtt || 'unknown',
    };
  }

  /**
   * Get storage diagnostics
   */
  async getStorageDiagnostics() {
    const dbStats = await db.getStats();
    let storageEstimate = null;

    if (navigator.storage && navigator.storage.estimate) {
      try {
        storageEstimate = await navigator.storage.estimate();
      } catch (error) {
        // Storage estimate not available
      }
    }

    return {
      database: dbStats,
      storageEstimate,
      quotaExceeded: await recoveryManager.checkQuotaExceeded(),
    };
  }

  /**
   * Get cache diagnostics
   */
  async getCacheDiagnostics() {
    const cacheKeys = await db.getKeys(db.stores.CACHE);
    const metadataKeys = await db.getKeys(db.stores.METADATA);

    // Analyze cache by entity type
    const byType = {};
    for (const key of cacheKeys) {
      const type = key.split(':')[0];
      byType[type] = (byType[type] || 0) + 1;
    }

    return {
      totalEntries: cacheKeys.length,
      metadataEntries: metadataKeys.length,
      byType,
      invalidationRules: Object.keys(invalidationManager.getPatterns('athletes')).length,
    };
  }

  /**
   * Get queue diagnostics
   */
  async getQueueDiagnostics() {
    const stats = await mutationQueue.getStats();
    const validation = await mutationQueue.validateQueue();

    return {
      ...stats,
      validationIssues: validation.length,
      validation,
    };
  }

  /**
   * Get image diagnostics
   */
  async getImageDiagnostics() {
    const stats = await imageCacheService.getStats();

    return stats;
  }

  /**
   * Get recovery diagnostics
   */
  async getRecoveryDiagnostics() {
    const stats = await recoveryManager.getStats();
    const issues = await recoveryManager.checkRecoveryNeeded();

    return {
      ...stats,
      currentIssues: issues,
    };
  }

  /**
   * Get configuration diagnostics
   */
  getConfigDiagnostics() {
    return {
      ttl: OfflineConfig.ttl,
      stale: OfflineConfig.stale,
      retry: OfflineConfig.retry,
      queue: OfflineConfig.queue,
      storage: OfflineConfig.storage,
      images: OfflineConfig.images,
      diagnostics: {
        enabled: OfflineConfig.diagnostics.enabled,
        logLevel: OfflineConfig.diagnostics.logLevel,
      },
    };
  }

  /**
   * Get recent logs
   */
  getRecentLogs(count = 50) {
    return this.logs.slice(-count);
  }

  /**
   * Clear logs
   */
  clearLogs() {
    this.logs = [];
  }

  /**
   * Export diagnostics as JSON
   */
  async exportDiagnostics() {
    const diagnostics = await this.getDiagnostics();
    return JSON.stringify(diagnostics, null, 2);
  }

  /**
   * Run health check
   */
  async runHealthCheck() {
    const results = {
      healthy: true,
      checks: [],
    };

    // Check 1: Database accessible
    try {
      await db.getStats();
      results.checks.push({ name: 'database', status: 'pass' });
    } catch (error) {
      results.checks.push({ name: 'database', status: 'fail', error: error.message });
      results.healthy = false;
    }

    // Check 2: No quota exceeded
    const quotaExceeded = await recoveryManager.checkQuotaExceeded();
    if (quotaExceeded) {
      results.checks.push({ name: 'quota', status: 'fail', message: 'Quota exceeded' });
      results.healthy = false;
    } else {
      results.checks.push({ name: 'quota', status: 'pass' });
    }

    // Check 3: No corruption
    const corruption = await recoveryManager.checkCorruption();
    if (corruption) {
      results.checks.push({ name: 'corruption', status: 'fail', message: 'Corruption detected' });
      results.healthy = false;
    } else {
      results.checks.push({ name: 'corruption', status: 'pass' });
    }

    // Check 4: Queue integrity
    const queueValidation = await mutationQueue.validateQueue();
    if (queueValidation.length > 0) {
      results.checks.push({ name: 'queue', status: 'warn', issues: queueValidation.length });
    } else {
      results.checks.push({ name: 'queue', status: 'pass' });
    }

    // Check 5: Service worker active
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      results.checks.push({ name: 'serviceWorker', status: 'pass' });
    } else {
      results.checks.push({ name: 'serviceWorker', status: 'warn', message: 'No active service worker' });
    }

    return results;
  }

  /**
   * Get cache inspector data
   */
  async inspectCache(entityType = null) {
    const cacheKeys = await db.getKeys(db.stores.CACHE);
    const entries = [];

    const keysToInspect = entityType 
      ? cacheKeys.filter(key => key.startsWith(`${entityType}:`))
      : cacheKeys.slice(0, 100); // Limit to 100 for performance

    for (const key of keysToInspect) {
      try {
        const entry = await db.get(key, db.stores.CACHE);
        entries.push({
          key,
          hasData: !!entry?.data,
          hasMetadata: !!entry?.metadata,
          updatedAt: entry?.metadata?.updatedAt,
          expiresAt: entry?.metadata?.expiresAt,
          isExpired: entry?.metadata?.expiresAt ? entry.metadata.expiresAt < Date.now() : false,
          syncStatus: entry?.metadata?.syncStatus,
        });
      } catch (error) {
        entries.push({
          key,
          error: error.message,
        });
      }
    }

    return {
      total: cacheKeys.length,
      inspected: entries.length,
      entries,
    };
  }

  /**
   * Get queue inspector data
   */
  async inspectQueue() {
    const queueKeys = await db.getKeys(db.stores.SYNC_QUEUE);
    const entries = [];

    for (const key of queueKeys) {
      try {
        const entry = await db.get(key, db.stores.SYNC_QUEUE);
        entries.push({
          id: key,
          operation: entry?.data?.operation,
          entityType: entry?.data?.entityType,
          entityId: entry?.data?.entityId,
          status: entry?.metadata?.status,
          retryCount: entry?.metadata?.retryCount,
          timestamp: entry?.metadata?.timestamp,
          idempotencyKey: entry?.data?.idempotencyKey,
        });
      } catch (error) {
        entries.push({
          id: key,
          error: error.message,
        });
      }
    }

    return {
      total: queueKeys.length,
      entries,
    };
  }

  /**
   * Trigger manual recovery
   */
  async triggerManualRecovery() {
    this.log('info', 'recovery', 'Manual recovery triggered');
    return await recoveryManager.recover();
  }

  /**
   * Clear all offline data (dangerous)
   */
  async clearAllOfflineData() {
    this.log('warn', 'recovery', 'Manual clear all offline data triggered');
    return await recoveryManager.emergencyReset();
  }
}

// Export singleton instance
export const offlineDiagnostics = new OfflineDiagnostics();

// Expose to window for debugging in development
if (typeof window !== 'undefined' && OfflineConfig.diagnostics.enabled) {
  window.__OFFLINE_DIAGNOSTICS__ = offlineDiagnostics;
  console.log('[Diagnostics] Offline diagnostics exposed at window.__OFFLINE_DIAGNOSTICS__');
}
